import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { MOODS, moodState, moodTarget, onMood, setMood, type AtmosphereService, type Mood, type MoodId } from './mood';
import type { PostPipeline, Quality } from './post';
import { createPmndrsPipeline } from './postPmndrs';
import { contextWatch } from './renderer';
import { maxTouchStep, pixelRatioFor, PixelStepGovernor } from './resolution';
import { estimateGpuMemory, trianglesByGroup } from './budget';
import { publishQualityTier } from './qualityTier';
import { cullView, flushCulledInstances } from './models/culledInstances';
import { CLASSIC_PROPS, loadedModelPacks } from './models/modelLibrary';
import { flushWheelBatches, lodCamera, lodView, styledVehicleMeshFactory, updateVehicleLook } from './vehicleLook';
import { setCrowdView, updateCharacterLook } from './characters/characterLook';
import { VIEWPOINTS, viewState, viewpointFocus } from './viewpoints';
import { sceneOverride } from './sceneOverride';
import { parallelCompile, warmExtrasRoot, warmPrograms } from './shaderWarmup';
import { cssSize } from './cssSize';

/**
 * Lighting (dusk over Kessmouth) and the final render through the post stack (owner: world-artist).
 *
 * Other modules can replace the final render by providing a `renderPipeline` service; by default
 * this module provides its own (HDR + bloom + grade + film).
 */
export interface RenderPipeline {
  render(dt: number): void;
  /** Current quality tier. */
  readonly quality?: Quality;
  setQuality?(q: Quality | 'auto'): void;
  /** Highest tier 'auto' may climb to (phones: 'medium', docs/MOBILE.md). Default 'high'. */
  setAutoCeiling?(q: Quality): void;
  /** Touch device (phones, tablets): pixel-ratio policy and budget from resolution.ts. */
  setDeviceProfile?(p: { touch: boolean }): void;
  /**
   * Compile the programs the scene needs at the current tier before its first frame (main.ts, under the
   * loading screen: shaderWarmup.ts). Resolves when they are ready, or after a few seconds at the latest.
   * `viewsFirst`: run the views once (no draw, no sim step) so the meshes they build on their first update
   * are compiled too (not in lockstep, where only stepFrames may move anything).
   */
  prewarm?(viewsFirst?: boolean): Promise<void>;
}

declare module '../core/services' {
  interface ServiceMap {
    renderPipeline: RenderPipeline;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Frame the camera on a named viewpoint (null = back to the gameplay camera). Returns success. */
    viewpoint(name: string | null): boolean;
    /** Names accepted by viewpoint(). */
    viewpoints(): string[];
    /**
     * 'low' | 'medium' | 'high' fixes the tier; 'auto' adapts to frame rate (default). 'auto:<tier>' makes AUTO
     * change to that tier as a frame-rate drop would: its programs are compiled first, then it switches.
     */
    setQuality(q: string): void;
    getQuality(): string;
    /** Drawing buffer vs on-screen size of the game canvas, pixel ratio and camera aspect (docs/MOBILE.md). */
    renderViewport(): { buffer: [number, number]; css: [number, number]; pixelRatio: number; cameraAspect: number } | null;
    /**
     * Render cost read-out (docs/PERF.md "Phone performance"): tier, pixel ratio and touch AUTO step,
     * estimated GPU memory in MB (drawing buffer, post render targets, shadow map, textures, geometry)
     * and the triangles of the last frame's main pass per top-level scene group.
     */
    renderBudget(): Record<string, unknown> | null;
    /**
     * Post stack cost probe (docs/PERF.md "Post stack"): draws the current frame `frames` times through
     * the post stack, each waited out with a 1-pixel read-back, and returns the mean ms per frame.
     * `postOnly` draws an empty scene instead: what is left is the post stack's own cost.
     */
    postBench(frames?: number, postOnly?: boolean): { ms: number; stack: string; tier: string } | null;
    /** WebGL context loss state: { lost, losses, restores }. */
    contextState(): { lost: boolean; losses: number; restores: number } | null;
  }
}

/**
 * The post stack: pmndrs `postprocessing` (postPmndrs.ts) unless `?addonspost` asks for the older
 * three.js-addons chain (post.ts), kept for side-by-side checks on a phone (docs/PERF.md "Post stack"). That
 * one is fetched only when asked for (docs/PERF.md "Load time").
 */
function pmndrsPost(): boolean {
  return typeof location === 'undefined' || !new URLSearchParams(location.search).has('addonspost');
}

const FOG_COLOUR = 0x232a36;

/** Sky gradient of a mood used to light and reflect off wet surfaces (PMREM environment). */
/** The PMREM generator whose shaders warmEnvironmentShaders handed to the driver; dropped after the first environment. */
let envWarm: THREE.PMREMGenerator | null = null;

/**
 * Start compiling the PMREM's own shaders at boot (main.ts, before the systems are built), so the first
 * environment does not wait for them (docs/PERF.md "Boot: readiness instead of a fixed wait"): its GGX filter
 * loops over 256 samples and its link held the main thread ~0.4 s at the scene-lighting init. They compile on the
 * driver's threads while the city is built; createEnvironment's generator then finds the same programs in three's
 * cache (same source, same render-target state). Reaches into PMREMGenerator's internals, so any failure (a three
 * upgrade) just leaves the compile where it was.
 */
export function warmEnvironmentShaders(renderer: THREE.WebGLRenderer): void {
  if (envWarm) return;
  try {
    const gen = new THREE.PMREMGenerator(renderer);
    const priv = gen as unknown as { _setSize(size: number): void; _allocateTargets(): THREE.WebGLRenderTarget; _ggxMaterial: THREE.Material | null; _blurMaterial: THREE.Material | null; _lodMeshes: THREE.Mesh[] };
    priv._setSize(256); // fromScene's default size: the shaders are built for it
    const target = priv._allocateTargets();
    const plane = priv._lodMeshes?.[0]?.geometry;
    if (!priv._ggxMaterial || !priv._blurMaterial || !plane) {
      target.dispose();
      gen.dispose();
      return;
    }
    const scene = new THREE.Scene();
    for (const m of [priv._ggxMaterial, priv._blurMaterial]) scene.add(new THREE.Mesh(plane, m));
    // the programs depend on drawing into a (linear, untone-mapped) target, as fromScene does
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(target);
    try {
      void renderer.compileAsync(scene, new THREE.OrthographicCamera()).catch(() => undefined);
    } finally {
      renderer.setRenderTarget(prev);
      target.dispose();
    }
    envWarm = gen;
  } catch {
    envWarm = null;
  }
}

function createEnvironment(renderer: THREE.WebGLRenderer, sky: Mood['sky']): THREE.Texture {
  const envScene = new THREE.Scene();
  const skyGeo = new THREE.SphereGeometry(50, 32, 16);
  const v3 = (c: [number, number, number]): THREE.Vector3 => new THREE.Vector3(...c);
  const skyMat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: { zenith: { value: v3(sky.zenith) }, mid: { value: v3(sky.mid) }, horizon: { value: v3(sky.horizon) }, ground: { value: v3(sky.ground) }, glow: { value: v3(sky.glow) } },
    vertexShader: `varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
    fragmentShader: `varying vec3 vDir;
      uniform vec3 zenith, mid, horizon, ground, glow;
      void main(){
        float h = vDir.y;
        vec3 c = h > 0.0 ? mix(mix(horizon, mid, smoothstep(0.0, 0.25, h)), zenith, smoothstep(0.25, 1.0, h)) : mix(horizon * 0.4, ground, smoothstep(0.0, -0.3, h));
        // warm glow low in the west (last light at dusk, the town's sodium haze at night)
        float west = max(0.0, dot(normalize(vec3(-1.0, 0.0, 0.35)), normalize(vec3(vDir.x, 0.0, vDir.z))));
        c += glow * pow(west, 6.0) * smoothstep(0.35, 0.0, abs(h));
        gl_FragColor = vec4(c, 1.0);
      }`,
  });
  envScene.add(new THREE.Mesh(skyGeo, skyMat));
  const pmrem = new THREE.PMREMGenerator(renderer);
  const rt = pmrem.fromScene(envScene, 0.035);
  pmrem.dispose();
  envWarm?.dispose();
  envWarm = null;
  skyGeo.dispose();
  skyMat.dispose();
  return rt.texture;
}

/**
 * Pike's spray bay work lamps (src/garage): while garage mode is open `level` eases to 1 and the scene's own
 * lights become a workshop rig — the sun turns into a warm key light, the north-east rim light into a cool
 * fill from `fill` (the camera's side), the sky fill and exposure come up. Only the existing lights change
 * (no light is added, so no shader recompiles); at 0 it costs nothing.
 */
export const workLight = { level: 0, fill: new THREE.Vector3(0.5, 0.8, 0.3) };
const WORK_KEY = new THREE.Color(0xffd6a8);
const WORK_FILL = new THREE.Color(0xa8c4ff);

/** LOW's light balance (no shadows, no grade): unshadowed sun trimmed, sky fill kept, exposure eased. */
const LOW_SUN = 0.7;
const LOW_HEMI = 1.0;
const LOW_EXPOSURE = 0.92;

export function createSceneLightingSystem(): System {
  let ctx: GameContext;
  // Low, warm last light from the west-south-west; cool sky fill; a faint rim from the north-east.
  const sun = new THREE.DirectionalLight(0xffb98a, 2.3);
  const hemi = new THREE.HemisphereLight(0x8394bd, 0x3b3128, 2.1);
  const rim = new THREE.DirectionalLight(0x8aa0d0, 0.6);
  const SUN_DIR = new THREE.Vector3(-0.72, 0.5, 0.48).normalize();
  /** One PMREM environment per mood, built the first time that mood is shown. */
  const envs = new Map<MoodId, THREE.Texture>();
  let unsub: (() => void) | null = null;
  let unsubRestore: (() => void) | null = null;
  /** Shadow map edge per tier: MEDIUM (phones) a quarter of HIGH's memory; LOW has no shadows. */
  const SHADOW_SIZE: Record<Quality, number> = { low: 1024, medium: 1024, high: 2048 };
  let shadowSize = 2048;
  let mood: Mood = moodState.current;
  /** LOW's own light balance is applied (see applyLights). */
  let lowBalance = false;
  let workSeen = 0;
  /**
   * The mood's lights, fog and exposure. LOW renders straight to the screen: no shadows, so the sun
   * reaches every surface, and no grade, so the split tone, saturation and contrast never happen. Its
   * own balance folds the grade's tints into the light colours (linear, multiplied after the hex is
   * converted) and trims the sun and exposure, so LOW keeps MEDIUM's colour and depth instead of a
   * flatter, hotter picture. One light update when the tier changes; no per-frame cost.
   */
  const applyLights = (): void => {
    const m = mood;
    const c = ctx;
    sun.color.setHex(m.sun.color);
    sun.intensity = m.sun.intensity;
    SUN_DIR.set(...m.sun.dir).normalize();
    hemi.color.setHex(m.hemi.sky);
    hemi.groundColor.setHex(m.hemi.ground);
    hemi.intensity = m.hemi.intensity;
    rim.intensity = m.rim;
    let exposure = m.exposure;
    if (lowBalance) {
      const [sr, sg, sb] = m.grade.shadowTint;
      const [hr, hg, hb] = m.grade.highTint;
      sun.color.setRGB(sun.color.r * hr, sun.color.g * hg, sun.color.b * hb);
      hemi.color.setRGB(hemi.color.r * sr, hemi.color.g * sg, hemi.color.b * sb);
      hemi.groundColor.setRGB(hemi.groundColor.r * sr, hemi.groundColor.g * sg, hemi.groundColor.b * sb);
      sun.intensity *= LOW_SUN;
      hemi.intensity *= LOW_HEMI;
      exposure *= LOW_EXPOSURE;
    }
    const w = workLight.level;
    if (w > 0) {
      sun.color.lerp(WORK_KEY, w * 0.7);
      sun.intensity += (Math.max(sun.intensity, 2.6) - sun.intensity) * w;
      rim.color.setHex(0x8aa0d0).lerp(WORK_FILL, w);
      rim.intensity += 1.6 * w;
      rim.position.copy(workLight.fill).multiplyScalar(100);
      hemi.intensity += 1.2 * w;
      exposure *= 1 + 0.12 * w;
    } else {
      rim.color.setHex(0x8aa0d0);
      rim.position.set(60, 80, -60);
    }
    (c.scene.background as THREE.Color).setHex(m.fog.color);
    (c.scene.fog as THREE.FogExp2).color.setHex(m.fog.color);
    c.scene.environmentIntensity = m.envIntensity;
    if (c.render) c.render.renderer.toneMappingExposure = exposure;
  };
  const envFor = (m: Mood): THREE.Texture | null => {
    if (!ctx.render) return null;
    let env = envs.get(m.id);
    if (!env) {
      env = createEnvironment(ctx.render.renderer, m.sky);
      envs.set(m.id, env);
    }
    return env;
  };
  return {
    id: 'scene-lighting',
    order: 150,
    init(c) {
      ctx = c;
      // The PMREM environments were *rendered* into textures: after a context restore three.js gives
      // their render targets back empty, so build them again.
      if (c.render) {
        unsubRestore = contextWatch(c.render.renderer).onRestored(() => {
          for (const e of envs.values()) e.dispose();
          envs.clear();
          c.scene.environment = envFor(moodState.current);
        });
      }
      c.scene.background = new THREE.Color(FOG_COLOUR);
      c.scene.fog = new THREE.FogExp2(FOG_COLOUR, moodState.current.fog.density);
      sun.castShadow = true;
      sun.shadow.mapSize.set(2048, 2048);
      const sc = sun.shadow.camera;
      sc.left = -78;
      sc.right = 78;
      sc.top = 78;
      sc.bottom = -78;
      sc.near = 10;
      sc.far = 420;
      sun.shadow.bias = -0.0006;
      sun.shadow.normalBias = 0.06;
      rim.position.set(60, 80, -60);
      c.scene.add(sun, sun.target, hemi, rim);
      unsub = onMood((m) => {
        if (c.render) c.scene.environment = envFor(m);
        mood = m;
        applyLights();
      });
    },
    update() {
      const tier = ctx.services.tryGet('renderPipeline')?.quality;
      const low = tier === 'low';
      if (low !== lowBalance || workLight.level !== workSeen) {
        lowBalance = low;
        workSeen = workLight.level;
        applyLights();
      }
      const want = tier ? SHADOW_SIZE[tier] : shadowSize;
      if (want !== shadowSize) {
        shadowSize = want;
        sun.shadow.mapSize.set(want, want);
        // three.js allocates the map on first use at mapSize; drop the old one so it is rebuilt.
        sun.shadow.map?.dispose();
        sun.shadow.map = null;
      }
      // Keep the shadow frustum centred on what the camera is looking at (snapped to reduce shimmer).
      const focus = viewpointFocus() ?? ctx.services.tryGet('cameraRig')?.lookAt;
      if (!focus) return;
      const snap = 2;
      const tx = Math.round(focus.x / snap) * snap;
      const tz = Math.round(focus.z / snap) * snap;
      sun.target.position.set(tx, 0, tz);
      sun.position.set(tx + SUN_DIR.x * 200, SUN_DIR.y * 200, tz + SUN_DIR.z * 200);
      sun.target.updateMatrixWorld();
      const fog = ctx.scene.fog as THREE.FogExp2 | null;
      if (fog) fog.density = viewState.active?.fog ?? moodState.current.fog.density;
      const wide = viewState.active && viewState.active.distance > 400;
      const ext = wide ? 340 : 78;
      const sc = sun.shadow.camera;
      if (sc.right !== ext) {
        sc.left = -ext;
        sc.right = ext;
        sc.top = ext;
        sc.bottom = -ext;
        sc.far = wide ? 900 : 420;
        sc.updateProjectionMatrix();
      }
    },
    dispose() {
      unsub?.();
      unsubRestore?.();
      ctx.scene.remove(sun, sun.target, hemi, rim);
      sun.dispose();
      hemi.dispose();
      rim.dispose();
      for (const e of envs.values()) e.dispose();
    },
  };
}

const TIERS: Quality[] = ['low', 'medium', 'high'];
/** Objects in the interior being drawn (render(): a model landing in it is compiled ahead of its draw). */
let overrideObjects = 0;
const countOverrideObject = (): void => {
  overrideObjects++;
};
const viewProj = new THREE.Matrix4();
const viewFrustum = new THREE.Frustum();

export function createRenderSystem(): System {
  let ctx: GameContext;
  let post: PostPipeline | null = null;
  let auto = true;
  let slowFor = 0;
  let fastFor = 0;
  let elapsed = 0;
  let ceiling: Quality = 'high';
  let touch = false;
  /** Seconds left in which adapt() ignores frame times (after a context restore: shader recompiles). */
  let grace = 0;
  const governor = new PixelStepGovernor();
  /** The tier AUTO is compiling programs for before it switches (autoTier). */
  let pendingTier: Quality | null = null;
  /** A 1×1 target bound only while compiling for the post stack's tiers (warmTier). */
  let warmTarget: THREE.WebGLRenderTarget | null = null;
  /** prewarm's view pass: everything updates, nothing is drawn. */
  let noDraw = false;
  /** The last interior whose programs were handed to the driver ahead of its first draw. */
  let warmedOverride: THREE.Scene | null = null;
  let warmedObjects = 0;
  let unsubMood: (() => void) | null = null;
  let unsubRestore: (() => void) | null = null;

  /** The pixel ratio the current tier, setting, device and touch step ask for (resolution.ts). */
  function wantedRatio(tier: Quality): number {
    const r = ctx.render!;
    const css = cssSize(r.canvas);
    const cssW = css.w || window.innerWidth;
    const cssH = css.h || window.innerHeight;
    const dpr = window.devicePixelRatio || 1;
    governor.maxStep = maxTouchStep(dpr, cssW, cssH);
    return pixelRatioFor({ tier, auto, touch, dpr, cssW, cssH, step: governor.step });
  }

  /** Switch tier: the pixel ratio first, so the composer is built at the right size once. */
  function setTier(q: Quality): void {
    if (!post || !ctx.render) return;
    if (q !== post.quality) governor.settle();
    ctx.render.renderer.setPixelRatio(wantedRatio(q));
    post.setQuality(q);
  }

  /**
   * Compile the scene's programs as tier `q` draws them, without switching to it: MEDIUM and HIGH draw into
   * the post stack's HDR target (linear output, no tone mapping in the material) with shadows, LOW straight
   * to the screen (sRGB, tone mapped) without. Only the program keys depend on that state, so it is set for
   * the synchronous compile call and put back at once.
   */
  function warmTier(q: Quality, scene: THREE.Scene = ctx.scene, camera: THREE.Camera = ctx.camera): Promise<void> {
    const r = ctx.render!.renderer;
    const shadows = r.shadowMap.enabled;
    const target = r.getRenderTarget();
    r.shadowMap.enabled = q !== 'low';
    if (q !== 'low') warmTarget ??= new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: false });
    r.setRenderTarget(q === 'low' ? null : warmTarget);
    try {
      const extras = scene === ctx.scene ? warmExtrasRoot() : null;
      if (!extras) return warmPrograms(r, scene, camera, scene);
      return Promise.all([warmPrograms(r, scene, camera, scene), warmPrograms(r, extras, camera, scene)]).then(() => undefined);
    } finally {
      r.setRenderTarget(target);
      r.shadowMap.enabled = shadows;
    }
  }

  /**
   * AUTO's tier change. Dropping MEDIUM → LOW on a phone recompiled ~40 programs on one frame (4.1 s on the
   * dev box, docs/PERF.md): the new tier's programs are compiled first, while the old tier keeps drawing,
   * and the switch happens once they are ready.
   */
  function autoTier(q: Quality): void {
    if (pendingTier) return;
    pendingTier = q;
    void warmTier(q).then(() => {
      pendingTier = null;
      if (auto && post && post.quality !== q) setTier(q);
    });
  }

  const own: RenderPipeline = {
    render(dt) {
      if (!ctx.render || !post || noDraw) return;
      // Context lost (backgrounded iOS app, GPU reset): draw nothing until it is restored.
      if (contextWatch(ctx.render.renderer).lost) {
        ctx.render.renderer.info.reset();
        return;
      }
      elapsed += dt;
      post.setPixelRatio(wantedRatio(post.quality));
      // An interior (Sid's back room) is drawn instead of the city while it is up (sceneOverride.ts).
      if (sceneOverride.scene && sceneOverride.camera) {
        // A new interior, or models landing in it (the hideout's Meshy pack loads while you are inside): hand
        // all its programs to the driver at once before the draw, so they compile side by side
        // (KHR_parallel_shader_compile) instead of one after another inside it. An interior is a few hundred
        // objects at most, so counting them each frame is cheap.
        overrideObjects = 0;
        sceneOverride.scene.traverse(countOverrideObject);
        if (sceneOverride.scene !== warmedOverride || overrideObjects !== warmedObjects) {
          warmedOverride = sceneOverride.scene;
          warmedObjects = overrideObjects;
          void warmTier(post.quality, sceneOverride.scene, sceneOverride.camera);
        }
        post.render(sceneOverride.scene, sceneOverride.camera, elapsed);
      } else {
        warmedOverride = null;
        post.render(ctx.scene, ctx.camera, elapsed);
      }
      if (grace > 0) grace -= dt;
      // An interior's frames say nothing about the city's: a cheap yard would lift the pixel ratio or the tier
      // that the street then has to drop again (1.5 s of stutter on the way back).
      else if (sceneOverride.scene) slowFor = fastFor = 0;
      else if (auto && dt > 0) adapt(dt);
    },
    get quality() {
      return post?.quality ?? 'low';
    },
    setQuality(q) {
      if (q === 'auto') {
        auto = true;
        return;
      }
      auto = false;
      setTier(q);
    },
    setAutoCeiling(q) {
      ceiling = q;
      if (auto && post && TIERS.indexOf(post.quality) > TIERS.indexOf(q)) setTier(q);
    },
    setDeviceProfile(p) {
      touch = p.touch;
    },
    prewarm(viewsFirst = false) {
      if (!ctx.render || !post) return Promise.resolve();
      if (viewsFirst) {
        // One view pass without drawing and without a sim step: the views build their meshes (the traffic's
        // car bodies, the crowd) on their first update, so their materials are in the scene to compile.
        noDraw = true;
        try {
          ctx.loop.advance(0);
        } finally {
          noDraw = false;
        }
      }
      return warmTier(post.quality);
    },
  };

  /**
   * Drop a tier after ~3 s below 45 fps; climb back after ~8 s above 58 fps. On touch devices at
   * MEDIUM the pixel ratio adapts first (PixelStepGovernor): it steps down before the tier drops,
   * and steps up while the tier is at its ceiling.
   */
  function adapt(dt: number): void {
    if (!post) return;
    // Real frames arrive clamped to 0.25 s (FixedStepLoop.maxFrameDt); anything longer is a lockstep
    // stepFrames(n) batch, which says nothing about the frame rate.
    if (dt > 0.25) return;
    if (touch && post.quality === 'medium') {
      const move = governor.update(dt);
      if (move !== 0) {
        slowFor = 0;
        fastFor = 0;
        return;
      }
      if (governor.step > 0) {
        // Still above 1.0: the governor handles slowness before the tier may drop.
        slowFor = 0;
      }
    }
    const fps = 1 / dt;
    if (fps < 45) {
      slowFor += dt;
      fastFor = 0;
    } else if (fps > 58) {
      fastFor += dt;
      slowFor = 0;
    } else {
      slowFor = Math.max(0, slowFor - dt);
      fastFor = Math.max(0, fastFor - dt);
    }
    if (slowFor > 3) {
      slowFor = 0;
      if (post.quality === 'high') autoTier('medium');
      else if (post.quality === 'medium') autoTier('low');
    } else if (fastFor > 8) {
      fastFor = 0;
      if (post.quality === 'low' && ceiling !== 'low') autoTier('medium');
      else if (post.quality === 'medium' && ceiling === 'high') autoTier('high');
    }
  }

  function applyViewpoint(): void {
    const v = viewState.active;
    if (!v) return;
    const cam = ctx.camera;
    const pitch = (v.pitchDeg * Math.PI) / 180;
    const yaw = Math.PI + (v.yawDeg * Math.PI) / 180; // screen-up heading; PI = north
    const back = { x: -Math.sin(yaw), z: -Math.cos(yaw) };
    const horiz = Math.cos(pitch) * v.distance;
    cam.position.set(v.target.x + back.x * horiz, Math.sin(pitch) * v.distance, v.target.z + back.z * horiz);
    cam.lookAt(v.target.x, 0, v.target.z);
    const fov = v.fov ?? 34;
    if (cam.fov !== fov || cam.far < v.distance * 2) {
      cam.fov = fov;
      cam.far = Math.max(600, v.distance * 2);
      cam.updateProjectionMatrix();
    }
  }

  return {
    id: 'render',
    order: 790,
    async init(c) {
      ctx = c;
      if (c.render) {
        c.render.renderer.info.autoReset = false;
        c.render.renderer.setPixelRatio(wantedRatio('high'));
        const create = pmndrsPost() ? createPmndrsPipeline : (await import('./post')).createPostPipeline;
        post = create(c.render.renderer, c.scene, c.camera);
        unsubRestore = contextWatch(c.render.renderer).onRestored(() => {
          // three.js re-uploads geometry, textures and targets by itself; the composer is rebuilt so
          // nothing of the lost context's state is reused, and the first slow frames don't drop a tier.
          post?.rebuild();
          governor.settle();
          slowFor = 0;
          fastFor = 0;
          grace = 2;
        });
        unsubMood = onMood((m) => post?.setMood(m));
        if (!c.services.tryGet('vehicleMeshFactory')) c.services.provide('vehicleMeshFactory', styledVehicleMeshFactory);
        if (!c.services.tryGet('renderPipeline')) c.services.provide('renderPipeline', own);
      }
      c.hooks.register('viewpoint', (name) => {
        if (name === null) {
          viewState.active = null;
          viewState.name = null;
          if (ctx.camera.far > 600) {
            ctx.camera.far = 600;
            ctx.camera.updateProjectionMatrix();
          }
          return true;
        }
        const v = VIEWPOINTS[name];
        if (!v) return false;
        viewState.active = v;
        viewState.name = name;
        return true;
      });
      c.hooks.register('viewpoints', () => Object.keys(VIEWPOINTS));
      const atmosphere: AtmosphereService = {
        get mood() {
          return moodState.current.id;
        },
        moods: Object.values(MOODS).map((m) => ({ id: m.id, label: m.label })),
        setMood,
      };
      c.services.provide('atmosphere', atmosphere);
      c.hooks.register('setMood', (id) => {
        if (Object.prototype.hasOwnProperty.call(MOODS, id)) setMood(id as MoodId);
      });
      c.hooks.register('getMood', () => moodTarget().id);
      c.hooks.register('setQuality', (q) => {
        if (q === 'auto' || q === 'low' || q === 'medium' || q === 'high') own.setQuality!(q);
        // 'auto:<tier>': AUTO moves to that tier the way a slow / fast frame rate makes it (programs first).
        else if (q === 'auto:low' || q === 'auto:medium' || q === 'auto:high') {
          auto = true;
          autoTier(q.slice(5) as Quality);
        }
      });
      c.hooks.register('getQuality', () => `${own.quality ?? 'low'}${auto ? ' (auto)' : ''}`);
      c.hooks.register('renderViewport', () => {
        const r = c.render;
        if (!r) return null;
        const box = r.canvas.getBoundingClientRect();
        return { buffer: [r.canvas.width, r.canvas.height], css: [box.width, box.height], pixelRatio: r.renderer.getPixelRatio(), cameraAspect: c.camera.aspect };
      });
      c.hooks.register('renderBudget', () => {
        const r = c.render;
        if (!r || !post) return null;
        const mem = estimateGpuMemory(r.renderer, c.scene, post.targetBytes(), !touch);
        return {
          tier: post.quality,
          auto,
          touch,
          pixelRatio: r.renderer.getPixelRatio(),
          touchStep: governor.step,
          touchMaxStep: governor.maxStep,
          // KHR_parallel_shader_compile: the pre-warm compiles side by side (shaderWarmup.ts).
          parallelCompile: parallelCompile(r.renderer),
          programs: r.renderer.info.programs?.length ?? 0,
          buffer: [r.canvas.width, r.canvas.height],
          gpuMB: mem,
          triangles: trianglesByGroup(c.scene, c.camera),
          // Meshy world packs loaded so far (render/models): their textures are in gpuMB.textures already.
          models: loadedModelPacks().map((p) => ({ pack: p.name, ids: p.ids(), textureMB: Math.round((p.textureBytes() / (1024 * 1024)) * 10) / 10 })),
        };
      });
      c.hooks.register('postBench', (frames = 30, postOnly = false) => {
        const r = c.render;
        if (!r || !post) return null;
        // An interior up (the profile's yard, Sid's back room) is what the frame draws: bench that.
        const sc = postOnly ? new THREE.Scene() : (sceneOverride.scene ?? c.scene);
        const cam = sceneOverride.camera ?? c.camera;
        const gl = r.renderer.getContext();
        const px = new Uint8Array(4);
        const frame = (): void => {
          post!.render(sc, cam, elapsed);
          gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
        };
        frame();
        const t0 = performance.now();
        for (let i = 0; i < frames; i++) frame();
        return { ms: +((performance.now() - t0) / frames).toFixed(2), stack: pmndrsPost() ? 'pmndrs' : 'addons', tier: post.quality };
      });
      c.hooks.register('contextState', () => {
        const r = c.render;
        if (!r) return null;
        const w = contextWatch(r.renderer);
        return { lost: w.lost, losses: w.losses, restores: w.restores };
      });
    },
    update(dt) {
      if (!ctx.render) return;
      applyViewpoint();
      lodCamera.copy(ctx.camera.position);
      // Crowd view cull for the next frame's crowd writes (characterLook.ts).
      ctx.camera.updateMatrixWorld();
      viewProj.multiplyMatrices(ctx.camera.projectionMatrix, ctx.camera.matrixWorldInverse);
      viewFrustum.setFromProjectionMatrix(viewProj);
      setCrowdView(viewFrustum.planes);
      // Wheels: packed now, culled to the frustum of the frame about to be drawn (wheelBatch.ts).
      lodView.on = true;
      flushWheelBatches(viewFrustum);
      // Loose street props (bins, crates, cones …): the same, through the generic batch (render/models).
      cullView.on = true;
      flushCulledInstances(CLASSIC_PROPS ? null : viewFrustum);
      const pipeline = ctx.services.tryGet('renderPipeline') ?? own;
      const tier = pipeline.quality ?? own.quality ?? 'low';
      updateVehicleLook(tier);
      updateCharacterLook(tier);
      publishQualityTier(tier);
      pipeline.render(dt);
    },
    dispose() {
      unsubMood?.();
      unsubRestore?.();
      setCrowdView(null);
      lodView.on = false;
      cullView.on = false;
      post?.dispose();
      warmTarget?.dispose();
      viewState.active = null;
      viewState.name = null;
    },
  };
}
